import React, { useState, useEffect } from 'react';
import { Pane, Heading, Button, Spinner, Text, Tooltip, IconButton, EyeOpenIcon, EditIcon, majorScale } from 'evergreen-ui';
import { useSelector } from 'react-redux';
import appTypes from '../../../../constants/types';
import SectionCard from '../../../../components/section-card';
import { AccountDataField, BadgesDataField, LocationDataField, TextDataField } from '../../../../components/data-fields';
import accounts from '../../../../constants/accounts';
import SnipeAsset from '../../../../components/snipe-asset';
import { api } from '../../../../services/api';

export const OnboardingDetails = ({ ticket }) => {
  const [customFields, setCustomFields] = useState([]);
  const [decryptedFields, setDecryptedFields] = useState({});
  const [decrypting, setDecrypting] = useState(null);
  const role = useSelector((state) => state.authentication.user.role);
  const isAdmin = role && role.name === 'Administrator';
  const [editingJoinDate, setEditingJoinDate] = useState(false);
  const [joinDateValue, setJoinDateValue] = useState(ticket?.ticket_data?.join_date || '');
  const [savingJoinDate, setSavingJoinDate] = useState(false);

  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const { data } = await api.settings.get('onboarding_ticket');
        setCustomFields(data.custom_fields);
      } catch (error) {
        console.error("Error fetching custom fields:", error);
      }
    };

    fetchCustomFields();
  }, []);

  const timersRef = React.useRef({});

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(({ timeoutId, intervalId }) => {
        if (timeoutId) clearTimeout(timeoutId);
        if (intervalId) clearInterval(intervalId);
      });
    };
  }, []);

  const handleDecryptField = async (field) => {
    if (decryptedFields[field]) return;
    setDecrypting(field);
    try {
      const { data } = await api.encryption.decryptData(ticket.ticket_data);
      let timer = 15;
      const intervalId = setInterval(() => {
        setDecryptedFields((prev) => {
          if (!prev[field]) return prev;
          return {
            ...prev,
            [field]: { ...prev[field], timer: prev[field].timer - 1 }
          };
        });
      }, 1000);
      const timeoutId = setTimeout(() => {
        setDecryptedFields((prev) => {
          const { [field]: _, ...rest } = prev;
          return rest;
        });
        clearInterval(intervalId);
        delete timersRef.current[field];
      }, 15000);
      timersRef.current[field] = { timeoutId, intervalId };
      setDecryptedFields((prev) => ({
        ...prev,
        [field]: {
          data: data[field],
          timer,
        }
      }));
    } catch (error) {
      console.error('Decryption error:', error, error?.response);
    } finally {
      setDecrypting(null);
    }
  };

  const getDecrypted = (field) => decryptedFields[field] || null;

  const handleEditJoinDate = () => {
    setJoinDateValue(ticket?.ticket_data?.join_date || '');
    setEditingJoinDate(true);
  };

  const handleCancelJoinDate = () => {
    setEditingJoinDate(false);
    setJoinDateValue(ticket?.ticket_data?.join_date || '');
  };

  const handleSaveJoinDate = async () => {
    console.log(joinDateValue);
    setSavingJoinDate(true);
    try {
      await api.tickets.edit(ticket.id, 'join_date', joinDateValue);
      ticket.ticket_data.join_date = joinDateValue;
      setEditingJoinDate(false);
    } catch (error) {
      console.error('Failed to update join date', error);
    } finally {
      setSavingJoinDate(false);
    }
  };

  return (
    <>
      <SectionCard
        title={
          <Pane display="flex" alignItems="center" justifyContent="space-between">
            <Heading size={600}>Personal information</Heading>
          </Pane>
        }
      >
        <TextDataField
          name="First name"
          value={ticket && ticket.ticket_data.first_name} />
        <TextDataField
          name="Last name"
          value={ticket && ticket.ticket_data.last_name} />
        <TextDataField
          name="Personal email"
          value={ticket && ticket.ticket_data.personal_email} />
        <TextDataField
          name="Team"
          value={ticket && ticket.ticket_data.team.name} />
        <LocationDataField
          name="Location"
          countryCode={ticket && ticket.ticket_data.location.country_code}
          countryName={ticket && ticket.ticket_data.location.name} />
        {customFields.includes('mobile_number') && (
            <TextDataField
              name="Mobile number"
              value={ticket && ticket.ticket_data.mobile_number}
            />
          )}
          {customFields.includes('join_date') && (
            <Pane display="flex" alignItems="center">
              <TextDataField
                name="Join date"
                value={
                  editingJoinDate ? (
                    <input
                      type="date"
                      value={joinDateValue || ''}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={e => setJoinDateValue(e.target.value)}
                      style={{ marginRight: 8 }}
                    />
                  ) : (ticket && ticket.ticket_data.join_date)
                }
              />
              {isAdmin && !editingJoinDate && ticket.status === 'pending' && (
                <Tooltip content="Edit join date" position="top">
                  <IconButton
                    marginLeft={majorScale(1)}
                    icon={EditIcon}
                    intent="info"
                    onClick={handleEditJoinDate}
                  />
                </Tooltip>
              )}
              {editingJoinDate && (
                <>
                  <Button
                    size="small"
                    appearance="primary"
                    marginLeft={majorScale(1)}
                    isLoading={savingJoinDate}
                    onClick={handleSaveJoinDate}
                    disabled={savingJoinDate || !joinDateValue}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    marginLeft={majorScale(1)}
                    onClick={handleCancelJoinDate}
                    disabled={savingJoinDate}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Pane>
          )}
          {customFields.includes('experience') && (
            <TextDataField
              name="Experience"
              value={ticket && ticket.ticket_data.experience}
            />
          )}
          {customFields.includes('job_title') && (
            <TextDataField
              name="Job title"
              value={ticket && ticket.ticket_data.job_title}
            />
          )}
          {customFields.includes('business_unit') && (
            <TextDataField
              name="Business unit"
              value={ticket && ticket.ticket_data.business_unit}
            />
          )}
          {customFields.includes('department') && (
            <TextDataField
              name="Department"
              value={ticket && ticket.ticket_data.team.name}
            />
          )}
          {customFields.includes('recruiter_name') && (
            <TextDataField
              name="Recruiter Name"
              value={ticket && ticket.ticket_data.recruiter_name}
            />
          )}
          {customFields.includes('birth_date') && (
            <TextDataField
              name="Date of Birth"
              value={ticket && ticket.ticket_data.birth_date}
            />
          )}
          {customFields.includes('reporting_manager') && (
            <TextDataField
              name="Reporting manager"
              value={ticket && ticket.ticket_data.reporting_manager}
            />
          )}
          {customFields.includes('identification') && (
            <TextDataField
              name="ID"
              value={
                getDecrypted('identification')
                  ? (
                    <Pane display="flex" alignItems="center">
                      <Text marginRight={majorScale(1)}>{getDecrypted('identification').data}</Text>
                        <Pane display="flex" alignItems="center">
                          <IconButton
                            marginLeft={majorScale(1)}
                            icon={EyeOpenIcon}
                            intent="info"
                            disabled={true}
                            onClick={undefined}
                          />
                          <Text marginLeft={majorScale(1)} color="muted" size={300}>
                            {getDecrypted('identification').timer}s
                          </Text>
                        </Pane>
                    </Pane>
                  )
                  : (
                    <Pane display="flex" alignItems="center">
                      <Text color="muted">Encrypted</Text>
                      <Tooltip
                        content={isAdmin ? (decrypting === 'identification' ? 'Decrypting...' : 'Click to decrypt') : 'Encrypted'}
                        position="top"
                      >
                        <IconButton
                          marginLeft={majorScale(1)}
                          icon={EyeOpenIcon}
                          intent="info"
                          disabled={!!decrypting}
                          onClick={isAdmin ? () => handleDecryptField('identification') : undefined}
                        />
                      </Tooltip>
                    </Pane>
                  )
              }
            />
          )}

          {customFields.includes('address') && (
            <TextDataField
              name="Address"
              value={
                getDecrypted('address')
                  ? (
                    <Pane display="flex" alignItems="center">
                      <Text marginRight={majorScale(1)}>{getDecrypted('address').data}</Text>
                        <Pane display="flex" alignItems="center">
                          <IconButton
                            marginLeft={majorScale(1)}
                            icon={EyeOpenIcon}
                            intent="info"
                            disabled={true}
                            onClick={undefined}
                          />
                          <Text marginLeft={majorScale(1)} color="muted" size={300}>
                            {getDecrypted('address').timer}s
                          </Text>
                        </Pane>
                    </Pane>
                  )
                  : (
                    <Pane display="flex" alignItems="center">
                      <Text color="muted">Encrypted</Text>
                      <Tooltip
                        content={isAdmin ? (decrypting === 'address' ? 'Decrypting...' : 'Click to decrypt') : 'Encrypted'}
                        position="top"
                      >
                        <IconButton
                          marginLeft={majorScale(1)}
                          icon={EyeOpenIcon}
                          intent="info"
                          disabled={!!decrypting}
                          onClick={isAdmin ? () => handleDecryptField('address') : undefined}
                        />
                      </Tooltip>
                    </Pane>
                  )
              }
            />
          )}
      </SectionCard>
      <SectionCard title="Email">
        <TextDataField
          name="Employee email"
          value={ticket && ticket.ticket_data.jampp_email} />
        <TextDataField
          name="Organizational unit"
          value={ticket && ticket.ticket_data.google_org_unit} />
        <BadgesDataField
          name="Email groups"
          items={ticket && ticket.ticket_data.email_groups}
          emptyMessage="No email groups" />
        <BadgesDataField
          name="Predefined emails"
          items={ticket && ticket.ticket_data.predefined_emails && ticket.ticket_data.predefined_emails.map((pe) => pe.name)}
          emptyMessage="No predefined emails will be sent" />
      </SectionCard>
      <SectionCard title="Accounts">
        {ticket.ticket_data.accounts.length < 1
          ? <Text color="muted">No accounts</Text>
          : accounts.map((account) => {
            if (ticket.ticket_data.accounts.includes(account.key)) {
              return (
                <AccountDataField
                  key={account.key}
                  image={account.image}
                  name={account.name} />
              );
            }
            return null;
          })}
      </SectionCard>
      <SectionCard title="Assets">
        {ticket.ticket_data.assets.length < 1
          ? <Text color="muted">No assets</Text>
          : ticket.ticket_data.assets.map((asset) => (
            <SnipeAsset key={asset.id} asset={asset} isSelectable={false} />
          ))}
      </SectionCard>
    </>
  );
}

OnboardingDetails.propTypes = {
  ticket: appTypes.ticket.props,
};
OnboardingDetails.defaultProps = {
  ticket: appTypes.ticket.default,
};
